import { queryOptions, useQuery } from '@tanstack/react-query';
import { TruckRecordingDTO, TruckRecordingService } from '../../generated';
import { ActivityRecording } from '../../../components/LiveView/ActivityTile';

export const todaysTruckRecordingsKeys = {
  base: () => ['pastActivities'] as const,
  one: ({ jobsiteId }: TodaysTruckRecordingsOptionsProps) => [...todaysTruckRecordingsKeys.base(), jobsiteId] as const,
};

interface TodaysTruckRecordingsOptionsProps {
  jobsiteId: string;
}

export function todaysActivitiesOptions({
  jobsiteId,
  enabled,
}: TodaysTruckRecordingsOptionsProps & { enabled: boolean }) {
  return queryOptions({
    queryKey: todaysTruckRecordingsKeys.one({ jobsiteId }),
    queryFn: async () => {
      const startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);

      const now = new Date().toISOString();
      const todayRange = {
        gte: startOfDay.toISOString(),
        lt: now,
      };
      const response = await TruckRecordingService.truckRecordingControllerGetRecordings({
        path: { jobsiteId: jobsiteId || '' },
        query: {
          filter: [
            {
              motion_start_at: { range: todayRange },
            },
          ],
          limit: 50,
          offset: 0,
          recordingsSorting: {
            sortDirection: 'desc',
            sortField: 'motionStartAt',
          },
        },
      });

      if (response.data !== undefined) {
        const activityRecordings = response.data.data.map(toActivityRecording);
        const licensePlates: Record<string, string> = {};
        response.data.data.forEach((recording) => {
          if (recording.capture_start_at && recording.license_plate) {
            licensePlates[recording.capture_start_at] = recording.license_plate;
          }
        });
        return { ...response.data, data: { activities: activityRecordings, licensePlates } };
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId && enabled,
  });
}

export function useTodaysActivities(props: TodaysTruckRecordingsOptionsProps & { enabled: boolean }) {
  return useQuery(todaysActivitiesOptions(props));
}

function toActivityRecording(recording: TruckRecordingDTO): ActivityRecording {
  return {
    id: recording.id,
    captureStartAt: recording.capture_start_at,
    imageStatus: 'finished',
    materialStatus: 'finished',
    edgeStatus: 'finished',
    volumeStatus: 'finished',
    soilStatus: 'finished',
    licensePlateStatus: 'finished',
    licensePlate: recording.license_plate || '',
  };
}
